import store from 'containers/store'
import { Map } from 'immutable'
import { PARDOT_APP_ID, ELOQUA_APP_ID, PIESYNC_APP_ID } from 'constants/ResourcesConstants'

export const Gates = {
  PREMIUM: 'premium_drift',
  PREMIUM_TEAM: 'premium_team',
  PREMIUM_ENTERPRISE: 'premium_enterprise',
  PLAYBOOKS: 'PLAYBOOKS',
  LEGACY_ROUTINGBOT: 'has_legacy_routingbot',
  PLAYBOOKS_DEV: 'has_playbooks_dev',
  MARKETO: 'marketo',
  ELOQUA: 'HAS_ELOQUA',
  PARDOT: 'HAS_PARDOT',
  DEMANDBASE: 'demandbase',
  UNLIMITED_SEATS: 'hasUnlimitedSeats',
  SALESFORCE_EVENTS: 'hasSalesforceEvents',
  KNOWLEDGE_BASE: 'has_grandfathered_knowledgebase',
  STANDARD_APRIL_2018: 'standard_04_2018',
  CLEARBIT_SETTINGS: 'clearbit_settings',
  SAVED_REPLIES: 'saved_replies',
  CALENDAR_DROP: 'calendar_drop',
  CALENDAR_INTEGRATION: 'calendar_integration',
  GRANDFATHERED_CALENDAR_INTEGRATION: 'has_meetings',
  HUBSPOT_INSTALL: 'has_hubspot_install',
  OUTREACH_BETA: 'has_outreach_beta',
  OUTREACH: 'has_outreach',
  HAS_MADKUDU: 'HAS_MADKUDU',
  HAS_PIESYNC: 'HAS_PIESYNC',
  LEANDATA: 'has_leandata',
  STRIPE: 'has_transaction_in_conversation',
  TEAM_VIDEO_LICENSE: 'has_team_video_license',
  ROUTING_API_CUTOVER: 'has_routing_api_cutover',
}

const createDevDriftMap = () => {
  const gateMap = {}
  gateMap[ELOQUA_APP_ID] = 'HAS_ELOQUA'
  gateMap[PARDOT_APP_ID] = 'HAS_PARDOT'
  gateMap[PIESYNC_APP_ID] = 'HAS_PIESYNC'
  return gateMap
}

export const DriftDevIntegrationGates = createDevDriftMap()

const FEATURE_ACCESS_LEVELS = Object.freeze({
  'READ': 0,
  'WRITE': 1
})

const NO_ARG_ERROR = `No arguments were provided.`
const ROUTE_BLACK_LIST = {
  org_admin: [],
  user: ['/settings/rules', '/settings/billing', '/settings/team'],
  agent: [
    '/settings/rules',
    '/settings/billing',
    '/settings/team',
    '/dashboard',
    '/customize',
    '/integrations',
    '/settings/manage-inboxes',
    '/install',
    '/routing',
    '/bot',
    '/leadbots',
  ],

  member: [], // hidden
}

const GATE_BLACK_LIST = {
  org_admin: [],
  user: ['rules_routing'],
  agent: ['rules_routing'],

  member: [], // hidden
}

const checkGateBlackList = gate => {
  const availableScopes = store.getState().getIn(['currentUser', 'user'], {}).scopes

  for (const scope in availableScopes) {
    const isBlackListed = _.includes(GATE_BLACK_LIST[availableScopes[scope]], gate)
    if (isBlackListed) {return false}
  }

  return true
}

const checkRouteBlackList = location => {
  const state = store.getState()
  const availableScopes = state.getIn(['currentUser', 'user'], {}).scopes

  for (const scope in availableScopes) {
    const isBlackListed = _.includes(ROUTE_BLACK_LIST[availableScopes[scope]], location)
    if (isBlackListed) {return false}
  }

  return true
}

const isOwner = () => {
  const userRole = store.getState().getIn(['currentUser', 'user'], {}).role
  return userRole === 'OWNER'
}

const checkRole = role => {
  const availableRoles = store.getState().getIn(['currentUser', 'user'], {}).roles
  return _.includes(availableRoles, role)
}

const hasAnyRole = Args => {
  const args = Array.isArray(Args[0]) ? Args[0] : [...Args]

  for (const arg in args) {
    if (checkRole(args[arg])) {
      return true
    }
  }
  return false
}

const checkScope = scope => {
  const availableScopes = store.getState().getIn(['currentUser', 'user'], {}).scopes
  return _.includes(availableScopes, scope)
}

const checkScopes = Args => {
  const args = Array.isArray(Args[0]) ? Args[0] : [...Args]

  if (!args.length) {
    throw NO_ARG_ERROR
  }

  for (const arg in args) {
    if (!checkScope(args[arg])) {
      return false
    }
  }
  return true
}

const checkAnyScopes = Args => {
  const args = Array.isArray(Args[0]) ? Args[0] : [...Args]

  if (!args.length) {
    throw NO_ARG_ERROR
  }

  for (const arg in args) {
    if (checkScope(args[arg])) {
      return true
    }
  }
  return false
}

const checkGate = gate => {
  const gates = store.getState().getIn(['currentUser', 'user'], {}).gates || Map()
  return !!gates.get(gate)
}

const checkGates = Args => {
  const args = Array.isArray(Args[0]) ? Args[0] : [...Args]

  if (!args.length) {
    throw NO_ARG_ERROR
  }

  for (const arg in args) {
    if (checkGate(args[arg])) {
      return true
    }
  }

  return false
}

const checkAllGates = (...Args) => {
  const args = Array.isArray(Args[0]) ? Args[0] : [...Args]

  if (!args.length) {
    return true
  }

  for (const arg in args) {
    if (!checkGate(args[arg])) {
      return false
    }
  }

  return true
}

const checkFeature = (feature) => {
  if (!feature) {
    return false
  }

  const { featureName, accessLevel } = feature
  const userFeatures = store.getState().getIn(['currentUser', 'user', 'features']) || {}

  const featureLevelMap = userFeatures[featureName]
  
  if (!featureLevelMap) {
    return false
  }

  const levelsForFeature = Object.keys(featureLevelMap)
  return levelsForFeature.some(level => FEATURE_ACCESS_LEVELS[level] >= FEATURE_ACCESS_LEVELS[accessLevel])
}

const checkFeatures = Args => {
  const args = Array.isArray(Args[0]) ? Args[0] : [...Args]

  for (const arg in args) {
    if (!checkFeature(args[arg])) {
      return false
    }
  }
  return true
}

const checkAnyFeatures = Args => {
  const args = Array.isArray(Args[0]) ? Args[0] : [...Args]

  for (const arg in args) {
    if (checkFeature(args[arg])) {
      return true
    }
  }
  return false
}

const checkDefaultRole = roleName => {
  const workspaceRoles = store.getState().getIn(['currentUser', 'user', 'workspaceRoles']) || []

  if (!workspaceRoles || !workspaceRoles.length) {
    return false
  }

  const defaultRoleNames = workspaceRoles.filter(wsRole => wsRole.role.isGlobal).map(wsRole => wsRole.role.name)
  return defaultRoleNames.includes(roleName)
}

const checkAnyDefaultRoles = Args => {
  const args = Array.isArray(Args[0]) ? Args[0] : [...Args]

  for (const arg in args) {
    if (checkDefaultRole(args[arg])) {
      return true
    }
  }
  return false
}

const hasAnyWorkspaceRoles = () => {
  const workspaceRoles = store.getState().getIn(['currentUser', 'user', 'workspaceRoles']) || []

  return workspaceRoles && workspaceRoles.length > 0
}

const ACCESS = {}
ACCESS.hasScopes = (...args) => checkScopes(args)
ACCESS.hasAnyScope = (...args) => checkAnyScopes(args)
ACCESS.hasAnyRole = (...args) => hasAnyRole(args)

ACCESS.hasGates = (...args) => checkGates(args)
ACCESS.hasAllGates = (...args) => checkAllGates(args),
ACCESS.hasNonBlacklistedGate = (...args) => checkGates(args) && checkGateBlackList(args)
ACCESS.hasAccess = location => checkRouteBlackList.call(this, location)

ACCESS.hasFeatures = (...args) => checkFeatures(args)
ACCESS.hasAnyFeature = (...args) => checkAnyFeatures(args)
ACCESS.hasAnyDefaultRole = (...args) => checkAnyDefaultRoles(args),

ACCESS.hasPaidGates = () => checkGates([Gates.PREMIUM, Gates.PREMIUM_TEAM, Gates.PREMIUM_ENTERPRISE])
ACCESS.isAtLeastProPlan = () => checkGates([Gates.PREMIUM_TEAM, Gates.PREMIUM_ENTERPRISE])
ACCESS.isCompanyPlan = () => checkGates([Gates.PREMIUM_ENTERPRISE])
ACCESS.isAdmin = () => checkScope('org_admin') || checkDefaultRole('Admin')
ACCESS.isAgent = () => checkScope('agent')
ACCESS.isUser = () => checkScope('user')
ACCESS.isAccountOwner = () => isOwner()
ACCESS.isCalendarUser = () => checkGate('has_calendar_user_license')
ACCESS.isMultiOrg = () => checkScope('multi_org')
ACCESS.isVideoOnlyUser = () => checkScope('video') && !checkScope('member') && !checkGate('has_calendar_user_license')
ACCESS.isChatUser = () => !checkScope('org_admin') && checkScope('member') && checkAnyScopes(['convo-ONLY_MINE', 'convo-WITH_NO_PARTICIPANTS', 'convo-ALL'])
ACCESS.isPlaybooksUser = () => checkRole('Playbooks')
ACCESS.isVideoAdmin = () => checkRole('Video Admin') && checkGate(Gates.TEAM_VIDEO_LICENSE)
ACCESS.isAnyAdmin = () => ACCESS.isAdmin() || ACCESS.isVideoAdmin() 
ACCESS.isRbacUser = () => checkScope('rbac_user')

ACCESS.hasAllOrgIntegrations = () => 
  ACCESS.isAnyAdmin() || 
  ACCESS.hasFeatures({
    featureName: "org_integrations",
    accessLevel: "READ"
  })

export default ACCESS



// WEBPACK FOOTER //
// ./src/scripts/utils/Access.js